import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { MaterialIcons } from '@expo/vector-icons'
import { DS } from '@/design-system'
import { useStore } from '@/models'
import { LoungeAvatar } from '../../components/LoungeAvatar'
import { hasOwnerRights } from '@ppwr/convex/lounges/shared'
import { translate } from '../../i18n'
import { SettingsScreenLayout, SettingsCenteredMessage, SettingsLoadingView } from '@/components/layouts/SettingsScreenLayout'
import { SettingsCard } from '@/components/layouts/SettingsCard'

const TABLE_COLOR_PRESETS = [
  { label: 'Default', color: null as string | null },
  { label: 'Emerald', color: '#1B6B4A' },
  { label: 'Navy', color: '#1E3A5F' },
  { label: 'Royal', color: '#4A2070' },
  { label: 'Slate', color: '#3D4F5F' },
  { label: 'Charcoal', color: '#2E2E38' },
  { label: 'Teal', color: '#1A5C5A' },
  { label: 'Crimson', color: '#8B1A1A' }
]

const DEFAULT_TABLE_COLOR = '#812A50'

const GRADIENT_PRESETS = [
  { label: 'Maroon', color1: '#6B1D2A', color2: '#E8A0B0' },
  { label: 'Ocean', color1: '#1A3B6B', color2: '#A78BFA' },
  { label: 'Sunset', color1: '#D97706', color2: '#FBBF24' },
  { label: 'Forest', color1: '#065F46', color2: '#6EE7B7' }
]

const SOLID_PRESETS = [
  { label: 'Maroon', color: '#6B1D2A' },
  { label: 'Blue', color: '#1E40AF' },
  { label: 'Pink', color: '#BE185D' },
  { label: 'Orange', color: '#C2410C' }
]

type AvatarOption_Config = {
  key: string
  label: string
  avatar:
    | { type: 'dicebear'; style: 'shapes' | 'rings' | 'thumbs' | 'funEmoji' }
    | { type: 'gradient'; color1: string; color2: string }
    | { type: 'solid'; color: string }
}

function buildAvatarOptions(): AvatarOption_Config[] {
  const dicebear: AvatarOption_Config[] = (['shapes', 'rings', 'thumbs', 'funEmoji'] as const).map((s) => ({
    key: `db-${s}`,
    label: s,
    avatar: { type: 'dicebear', style: s }
  }))
  const gradients: AvatarOption_Config[] = GRADIENT_PRESETS.map((g) => ({
    key: `grad-${g.color1}`,
    label: g.label,
    avatar: { type: 'gradient', color1: g.color1, color2: g.color2 }
  }))
  const solids: AvatarOption_Config[] = SOLID_PRESETS.map((s) => ({
    key: `solid-${s.color}`,
    label: s.label,
    avatar: { type: 'solid', color: s.color }
  }))
  return [...dicebear, ...gradients, ...solids]
}

const ALL_AVATAR_OPTIONS = buildAvatarOptions()

function isActiveAvatar(option: AvatarOption_Config['avatar'], current: any): boolean {
  if (!current) return false
  if (option.type !== current.type) return false
  if (option.type === 'dicebear' && current.type === 'dicebear') return option.style === current.style
  if (option.type === 'gradient' && current.type === 'gradient')
    return option.color1 === current.color1 && option.color2 === current.color2
  if (option.type === 'solid' && current.type === 'solid') return option.color === current.color
  return false
}

function AvatarOption({
  children,
  active,
  label,
  onPress
}: { children: React.ReactNode; active: boolean; label: string; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={styles.avatarOption}>
      <View style={[styles.avatarOptionRing, active && styles.avatarOptionRingActive]}>{children}</View>
      <DS.Text
        variant="Mini/Regular"
        style={{ color: DS.palette['Brand/Dark Grey'], textAlign: 'center' }}
        numberOfLines={1}
      >
        {label}
      </DS.Text>
    </Pressable>
  )
}

function AvatarPicker({
  lobby,
  loungeId,
  userId,
  updateAvatar
}: { lobby: any; loungeId: Id<'lounges'>; userId: string; updateAvatar: any }) {
  const scrollRef = useRef<ScrollView>(null)
  const currentAvatar = lobby.branding.avatar
  const sorted = [...ALL_AVATAR_OPTIONS].sort((a, b) => {
    const aActive = isActiveAvatar(a.avatar, currentAvatar) ? 0 : 1
    const bActive = isActiveAvatar(b.avatar, currentAvatar) ? 0 : 1
    return aActive - bActive
  })

  const handleSelect = useCallback(
    (avatar: AvatarOption_Config['avatar']) => {
      updateAvatar({ loungeId, actorId: userId, avatar })
      scrollRef.current?.scrollTo({ x: 0, animated: true })
    },
    [loungeId, userId, updateAvatar]
  )

  return (
    <View style={styles.pickerWrap}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.avatarRow}
      >
        {sorted.map((opt) => (
          <AvatarOption
            key={opt.key}
            label={opt.label}
            active={isActiveAvatar(opt.avatar, currentAvatar)}
            onPress={() => handleSelect(opt.avatar)}
          >
            <LoungeAvatar name={lobby.name} branding={{ avatar: opt.avatar }} size={56} />
          </AvatarOption>
        ))}
      </ScrollView>
    </View>
  )
}

function TableColorPicker({
  lobby,
  loungeId,
  userId,
  updateTableColor
}: { lobby: any; loungeId: Id<'lounges'>; userId: string; updateTableColor: any }) {
  const scrollRef = useRef<ScrollView>(null)
  const currentColor = lobby.branding.primaryColor ?? null
  const sorted = [...TABLE_COLOR_PRESETS].sort((a, b) => {
    const aActive = a.color === currentColor ? 0 : 1
    const bActive = b.color === currentColor ? 0 : 1
    return aActive - bActive
  })

  return (
    <View style={styles.pickerWrap}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.avatarRow}
      >
        {sorted.map((preset) => {
          const isActive = preset.color === currentColor
          const displayColor = preset.color ?? DEFAULT_TABLE_COLOR
          return (
            <Pressable
              key={preset.label}
              onPress={() => {
                updateTableColor({ loungeId, actorId: userId, primaryColor: preset.color })
                scrollRef.current?.scrollTo({ x: 0, animated: true })
              }}
              style={styles.avatarOption}
            >
              <View style={[styles.avatarOptionRing, isActive && styles.avatarOptionRingActive]}>
                <View style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: displayColor }} />
              </View>
              <DS.Text
                variant="Mini/Regular"
                style={{ color: DS.palette['Brand/Dark Grey'], textAlign: 'center' }}
                numberOfLines={1}
              >
                {preset.label}
              </DS.Text>
            </Pressable>
          )
        })}
      </ScrollView>
    </View>
  )
}

export function LoungeBrandingScreen() {
  const { loungeId: loungeIdStr } = useLocalSearchParams<{ loungeId: string }>()
  const loungeId = loungeIdStr as Id<'lounges'> | undefined
  const router = useRouter()
  const {
    session: { user }
  } = useStore()
  const userId = user?.auth0Id ?? ''

  const lobby = useQuery(api.lounges.queries.getLoungeLobby, loungeId ? { loungeId, userId } : 'skip')

  const updateAvatar = useMutation(api.lounges.mutations.updateLoungeAvatar)
  const updateTableColor = useMutation(api.lounges.mutations.updateLoungeTableColor)
  const updateName = useMutation(api.lounges.mutations.updateLoungeName)
  const updateDescription = useMutation(api.lounges.mutations.updateLoungeDescription)

  const callerMember = lobby?.members.find((m) => m.userId === userId)
  const role = callerMember?.role
  const isOwnerOrCoowner = useMemo(() => role && hasOwnerRights(role), [role])

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [editingName, setEditingName] = useState(false)
  const lastSyncedNameRef = useRef<string | null>(null)
  const lastSyncedDescriptionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!lobby) return
    if (lastSyncedNameRef.current !== lobby.name) {
      setName(lobby.name)
      lastSyncedNameRef.current = lobby.name
    }
    const serverDescription = lobby.description ?? ''
    if (lastSyncedDescriptionRef.current !== serverDescription) {
      setDescription(serverDescription)
      lastSyncedDescriptionRef.current = serverDescription
    }
  }, [lobby])

  const saveName = useCallback(async () => {
    if (!loungeId || !lobby) return
    const trimmed = name.trim()
    setEditingName(false)
    if (trimmed.length === 0) {
      setName(lobby.name)
      return
    }
    if (trimmed === lobby.name) return
    try {
      await updateName({ loungeId: loungeId as Id<'lounges'>, actorId: userId, name: trimmed })
      lastSyncedNameRef.current = trimmed
    } catch (err: any) {
      Alert.alert(translate('common.error'), err.message ?? translate('loungeBrandingScreen.errors.updateNameFailed'))
      setName(lobby.name)
    }
  }, [loungeId, lobby, userId, name, updateName])

  const saveDescription = useCallback(async () => {
    if (!loungeId || !lobby) return
    const trimmed = description.trim()
    const current = lobby.description ?? ''
    if (trimmed === current) return
    try {
      await updateDescription({
        loungeId: loungeId as Id<'lounges'>,
        actorId: userId,
        description: trimmed.length === 0 ? null : trimmed
      })
      lastSyncedDescriptionRef.current = trimmed
    } catch (err: any) {
      Alert.alert(
        translate('common.error'),
        err.message ?? translate('loungeBrandingScreen.errors.updateDescriptionFailed')
      )
      setDescription(current)
    }
  }, [loungeId, lobby, userId, description, updateDescription])

  if (!lobby) {
    return (
      <SettingsScreenLayout onBack={() => router.back()}>
        <SettingsLoadingView />
      </SettingsScreenLayout>
    )
  }

  if (!isOwnerOrCoowner) {
    return (
      <SettingsScreenLayout title={translate('loungeBrandingScreen.title')} onBack={() => router.back()}>
        <SettingsCenteredMessage message={translate('loungeBrandingScreen.accessDenied')} />
      </SettingsScreenLayout>
    )
  }

  return (
    <SettingsScreenLayout title={translate('loungeBrandingScreen.title')} onBack={() => router.back()}>
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        <SettingsCard gap={20}>
          {/* Lounge Name */}
          <View style={styles.field}>
            <DS.Text variant="Mini/Bold" style={styles.sectionLabel}>
              {translate('loungeBrandingScreen.loungeName.sectionLabel')}
            </DS.Text>
            <View style={styles.inputContainer}>
              <TextInput
                value={name}
                onChangeText={setName}
                onFocus={() => setEditingName(true)}
                onBlur={saveName}
                onSubmitEditing={saveName}
                returnKeyType="done"
                editable
                placeholder={translate('loungeBrandingScreen.loungeName.placeholder')}
                placeholderTextColor={DS.palette['Brand/Dark Grey']}
                style={styles.nameInput}
                maxLength={60}
              />
              {!editingName && (
                <Pressable
                  onPress={() => setEditingName(true)}
                  style={styles.editIcon}
                  accessibilityLabel={translate('loungeBrandingScreen.loungeName.editAccessibilityLabel')}
                >
                  <MaterialIcons name="edit" size={18} color={DS.palette['Global/Black']} />
                </Pressable>
              )}
            </View>
          </View>

          {/* Short Description */}
          <View style={styles.field}>
            <DS.Text variant="Mini/Bold" style={styles.sectionLabel}>
              {translate('loungeBrandingScreen.description.sectionLabel')}
            </DS.Text>
            <View style={styles.inputContainer}>
              <TextInput
                value={description}
                onChangeText={setDescription}
                onBlur={saveDescription}
                placeholder={translate('loungeBrandingScreen.description.placeholder')}
                placeholderTextColor={DS.palette['Brand/Dark Grey']}
                multiline
                maxLength={280}
                style={styles.descriptionInput}
              />
            </View>
          </View>

          {/* Avatar Picker */}
          <View style={styles.field}>
            <DS.Text variant="Mini/Bold" style={styles.sectionLabel}>
              {translate('loungeBrandingScreen.avatar.sectionLabel')}
            </DS.Text>
            {lobby.branding.logoThumbS3Key ? (
              <DS.Text variant="Body Large/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
                {translate('loungeBrandingScreen.avatar.uploadedLogoMessage')}
              </DS.Text>
            ) : (
              <AvatarPicker
                lobby={lobby}
                loungeId={loungeId as Id<'lounges'>}
                userId={userId}
                updateAvatar={updateAvatar}
              />
            )}
          </View>

          {/* Table Color */}
          <View style={styles.field}>
            <DS.Text variant="Mini/Bold" style={styles.sectionLabel}>
              {translate('loungeBrandingScreen.tableColor.sectionLabel')}
            </DS.Text>
            <TableColorPicker
              lobby={lobby}
              loungeId={loungeId as Id<'lounges'>}
              userId={userId}
              updateTableColor={updateTableColor}
            />
          </View>
        </SettingsCard>
      </ScrollView>
    </SettingsScreenLayout>
  )
}

const styles = StyleSheet.create({
  content: {
    padding: 16
  },
  field: {
    gap: 8
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: 0.5,
    color: DS.palette['Brand/Dark Grey']
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: DS.palette['Global/Black'],
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 12
  },
  editIcon: {
    padding: 4
  },
  nameInput: {
    flex: 1,
    fontSize: 16,
    color: DS.palette['Global/Black'],
    paddingVertical: 0
  },
  descriptionInput: {
    flex: 1,
    fontSize: 16,
    color: DS.palette['Global/Black'],
    minHeight: 24,
    paddingVertical: 0,
    textAlignVertical: 'top'
  },
  pickerWrap: {
    marginHorizontal: -4
  },
  avatarRow: {
    gap: 12,
    paddingVertical: 4,
    paddingHorizontal: 4
  },
  avatarOption: {
    alignItems: 'center',
    gap: 4,
    width: 64
  },
  avatarOptionRing: {
    borderRadius: 30,
    borderWidth: 2,
    borderColor: 'transparent',
    padding: 1
  },
  avatarOptionRingActive: {
    borderColor: DS.palette['Brand/Maroon']
  }
})
